import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";

import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

import { useStore } from "@Store/";

import Container from "@components/Container";
import RichText from "@components/RichText";

import {
  BeastItem,
  BeastItemContent,
  BeastItems,
  BeastMain,
  BeastTitle,
  BeastWrapper,
  ItemTitle,
} from "./index.style";

gsap.registerPlugin(ScrollTrigger);

const Beast = ({ list, id }) => {
  const [store] = useStore();

  const sectionRef = useRef(null);
  const itemsRef = useRef([]);
  const smoothScrollRef = useRef(store?.smoothScroll);

  const sectionId = id || "beast";
  const displayedItems = list.slice(0, 4);

  smoothScrollRef.current = store?.smoothScroll;

  const addItemRef = (element, index) => {
    itemsRef.current[index] = element;
  };

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const section = sectionRef.current;
    const items = itemsRef.current.filter(Boolean);

    if (!section || !items.length) {
      return undefined;
    }

    let firstFrameId;
    let secondFrameId;

    const refresh = () => {
      if (
        smoothScrollRef.current &&
        typeof smoothScrollRef.current.refresh === "function"
      ) {
        smoothScrollRef.current.refresh();
      } else {
        ScrollTrigger.refresh();
      }
    };

    const refreshAfterLayout = () => {
      if (firstFrameId) {
        cancelAnimationFrame(firstFrameId);
      }

      if (secondFrameId) {
        cancelAnimationFrame(secondFrameId);
      }

      firstFrameId = requestAnimationFrame(() => {
        secondFrameId = requestAnimationFrame(() => {
          refresh();
        });
      });
    };

    const handleResize = () => {
      gsap.matchMediaRefresh();
      refreshAfterLayout();
    };

    const ctx = gsap.context(() => {
      const mm = gsap.matchMedia();

      mm.add("(min-width:1024px)", () => {
        const itemsTween = gsap.fromTo(
          items,
          {
            y: 100,
            autoAlpha: 0,
          },
          {
            y: 0,
            autoAlpha: 1,
            duration: 1,
            stagger: {
              each: 0.16,
              from: "start",
            },
            ease: "power3.out",
            immediateRender: true,
            clearProps:
              "transform,opacity,visibility,willChange",
            scrollTrigger: {
              id: `${sectionId}-items`,
              trigger: section,
              start: "top 80%",
              toggleActions: "play none none none",
              once: true,
              invalidateOnRefresh: true,
            },
          },
        );

        refreshAfterLayout();

        return () => {
          itemsTween.scrollTrigger?.kill();
          itemsTween.kill();
        };
      });

      mm.add("(max-width:1023px)", () => {
        gsap.set(items, {
          clearProps: "all",
        });
      });

      refreshAfterLayout();

      return () => {
        mm.revert();
      };
    }, section);

    window.addEventListener("load", refreshAfterLayout);
    window.addEventListener("resize", handleResize);

    return () => {
      if (firstFrameId) {
        cancelAnimationFrame(firstFrameId);
      }

      if (secondFrameId) {
        cancelAnimationFrame(secondFrameId);
      }

      ctx.revert();

      window.removeEventListener(
        "load",
        refreshAfterLayout,
      );
      window.removeEventListener("resize", handleResize);

      itemsRef.current = [];
    };
  }, [displayedItems.length, sectionId]);

  return (
    <BeastMain
      ref={sectionRef}
      id={sectionId}
      aria-labelledby={`${sectionId}-title`}
    >
      <BeastWrapper>
        <Container>
          <BeastTitle id={`${sectionId}-title`}>
            <span>A different</span>

            <strong>beast.</strong>
          </BeastTitle>
        </Container>

        {displayedItems.length > 0 && (
          <BeastItems>
            {displayedItems.map((item, index) => (
              <BeastItem
                ref={(element) =>
                  addItemRef(element, index)
                }
                key={
                  item?.id ||
                  item?.contentful_id ||
                  index
                }
                $index={index}
              >
                <BeastItemContent>
                  {item?.textOld?.textOld && (
                    <ItemTitle>
                      {item.textOld.textOld}
                    </ItemTitle>
                  )}

                  {item?.text && (
                    <RichText content={item.text} />
                  )}
                </BeastItemContent>
              </BeastItem>
            ))}
          </BeastItems>
        )}
      </BeastWrapper>
    </BeastMain>
  );
};

Beast.propTypes = {
  id: PropTypes.string,
  list: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      contentful_id: PropTypes.string,
      text: PropTypes.object,
      textOld: PropTypes.shape({
        textOld: PropTypes.string,
      }),
    }),
  ),
};

Beast.defaultProps = {
  id: "beast",
  list: [],
};

export default Beast;